import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  NbButtonModule,
  NbCardModule,
  NbDialogService,
  NbFormFieldModule,
  NbIconModule,
  NbInputModule,
  NbSpinnerModule,
  NbToastrService,
} from '@nebular/theme';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap } from 'rxjs/operators';
import { AuthService } from '../../core/auth/auth.service';
import { ChamadaTurmaDto } from '../../core/api/chamada-turma.dto';
import { DialogAlertComponent } from '../inscricao/inscricao.dialog.component';
import { Turma } from '../../core/api/turma';
import { FrequenciaService } from '../../core/api/frequencia.service';
import { TurmaService } from '../../core/api/turma.service';
import { TurmaUsuarioService } from '../../core/api/turma-usuario.service';

import {
  ChamadaPessoasDialogComponent,
  ModoChamadaPessoas,
} from './chamada-pessoas-dialog.component';
import { mensagemDeErro } from '../../utils/mensagem-erro';
import { ListaVaziaComponent } from '../../shared/lista-vazia/lista-vazia.component';
import { AutocompleteComponent, OpcaoAutocomplete } from '../../shared/autocomplete/autocomplete.component';
import { BarraCarregandoComponent } from '../../shared/barra-carregando/barra-carregando.component';
import { Ordenacao, OrdenavelComponent } from '../../shared/ordenavel/ordenavel.component';
import { ordenarEmMemoria } from '../../utils/ordenar-em-memoria';
/**
 * Chamadas realizadas por turma.
 *
 * Alcance por perfil: **admin** enxerga todas as turmas ativas; qualquer outro perfil enxerga
 * apenas as turmas em que tem vínculo `TurmaUsuario` (`GET /turmas-usuario/minhas`) — e, portanto,
 * só consegue excluir chamadas dessas turmas.
 *
 * Pontos de chamada (contrato igreja-api):
 * - `GET    /turmas?page&size`                 (admin — turmas para o autocomplete, só as ativas)
 * - `GET    /turmas-usuario/minhas`            (demais perfis)
 * - `GET    /frequencia/{turmaId}/chamadas`
 * - `DELETE /frequencia/{turmaId}/{data}`
 */
@Component({
  selector: 'app-chamadas',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterLink,
    NbCardModule,
    NbButtonModule,
    NbFormFieldModule,
    NbIconModule,
    NbInputModule,
    NbSpinnerModule,
    ListaVaziaComponent,
    AutocompleteComponent,
    BarraCarregandoComponent,
    OrdenavelComponent,
  ],
  templateUrl: './chamadas.component.html',
  styleUrl: './chamadas.component.scss',
})
export class ChamadasComponent implements OnInit {
  /**
   * `TAB-01` — ordenação **em memória**, e aqui ela é legítima: o endpoint devolve a
   * coleção inteira, não uma página. Onde há paginação, ordenar em memória mentiria.
   */
  ordenacao: Ordenacao | null = null;

  ordenarPor(o: Ordenacao): void {
    this.ordenacao = o;
  }

  get chamadasExibidas(): ChamadaTurmaDto[] {
    return ordenarEmMemoria(this.chamadas, this.ordenacao, (item, campo) => (item as unknown as Record<string, unknown>)[campo]);
  }

  /** Somente turmas ativas — é nelas que a chamada acontece. */
  turmasAtivas: Turma[] = [];
  turmaSelecionada = '';


  chamadas: ChamadaTurmaDto[] = [];
  carregandoTurmas = false;
  carregandoChamadas = false;
  excluindoData: string | null = null;
  erro: string | null = null;

  /** Falso ⇒ a lista está restrita às turmas vinculadas ao usuário (TurmaUsuario). */
  ehAdmin = false;

  depurando = false;

  constructor(
    private turmaService: TurmaService,
    private turmaUsuarioService: TurmaUsuarioService,
    private frequenciaService: FrequenciaService,
    private auth: AuthService,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
  ) {}

  ngOnInit(): void {
    this.carregarTurmas();
  }

  carregarTurmas(): void {
    this.carregandoTurmas = true;
    this.auth
      .ensurePerfil()
      .pipe(
        catchError(() => of(null)),
        switchMap((perfil) => {
          this.ehAdmin = perfil?.admin === true;
          return this.ehAdmin ? this.todasAsTurmas() : this.minhasTurmas();
        }),
        finalize(() => (this.carregandoTurmas = false)),
      )
      .subscribe({
        next: (lista) => {
          this.turmasAtivas = lista.filter((t) => t.ativo !== false);
        },
        error: (err) =>
          this.toastr.danger(mensagemDeErro(err, 'Não foi possível carregar as turmas.'), 'Erro'),
      });
  }

  private todasAsTurmas(): Observable<Turma[]> {
    return this.turmaService.listar(0, 500, '').pipe(map((res) => res.content ?? []));
  }

  private minhasTurmas(): Observable<Turma[]> {
    return this.turmaUsuarioService.minhasTurmas().pipe(map((res) => res ?? []));
  }

  /** Evita repetir o ano quando ele já faz parte do nome da turma. */
  labelTurma(t: Turma): string {
    const nome = (t.nome ?? '').trim();
    const ano = t.ano != null ? String(t.ano) : '';
    if (!ano || nome.includes(ano)) return nome;
    return `${nome} (${ano})`;
  }

  get opcoesTurma(): OpcaoAutocomplete[] {
    return this.turmasAtivas.map((t) => ({ id: t.id ?? '', rotulo: this.labelTurma(t) }));
  }

  selecionarTurma(id: string): void {
    this.turmaSelecionada = id;
    this.carregarChamadas();
  }

  trackByTurmaId = (_: number, t: Turma): string => t.id ?? '';
  trackByData = (_: number, c: ChamadaTurmaDto): string => c.data;

  carregarChamadas(): void {
    if (!this.turmaSelecionada) {
      this.chamadas = [];
      return;
    }
    this.erro = null;
    this.carregandoChamadas = true;
    this.chamadas = [];
    this.frequenciaService
      .listarChamadas(this.turmaSelecionada)
      .pipe(finalize(() => (this.carregandoChamadas = false)))
      .subscribe({
        next: (lista) => (this.chamadas = lista),
        error: (err) => {
          this.chamadas = [];
          this.erro = mensagemDeErro(err, 'Não foi possível carregar as chamadas desta turma.');
        },
      });
  }

  /** Quem esteve presente no dia — o total da coluna não diz quem. */
  verPresentes(c: ChamadaTurmaDto): void {
    this.abrirPessoasDaChamada(c, 'presentes');
  }

  /** Quem faltou no dia, com o WhatsApp de cada um à mão. */
  verFaltantes(c: ChamadaTurmaDto): void {
    this.abrirPessoasDaChamada(c, 'faltantes');
  }

  private abrirPessoasDaChamada(c: ChamadaTurmaDto, modo: ModoChamadaPessoas): void {
    if (!this.turmaSelecionada || !c?.data) return;

    this.dialogService.open(ChamadaPessoasDialogComponent, {
      context: {
        turmaId: this.turmaSelecionada,
        turmaNome: this.nomeTurmaSelecionada,
        data: c.data,
        modo,
      },
    });
  }

  get nomeTurmaSelecionada(): string {
    const t = this.turmasAtivas.find((x) => x.id === this.turmaSelecionada);
    return t ? this.labelTurma(t) : '';
  }

  /** `yyyy-MM-dd` → Date local, para o pipe `date`. */
  paraData(iso: string | undefined): Date | null {
    if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return null;
    const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
    return new Date(y, m - 1, d);
  }

  /** Chamada começada e não concluída: existe registro mas ninguém foi marcado presente. */
  semPresenca(c: ChamadaTurmaDto): boolean {
    return (c?.totalPresentes ?? 0) === 0;
  }

  get chamadasSemPresenca(): ChamadaTurmaDto[] {
    return this.chamadas.filter((c) => this.semPresenca(c));
  }

  /**
   * Depurador: apaga de uma vez todas as chamadas da turma sem nenhuma presença.
   * `POST /frequencia/{turmaId}/depurar-chamadas-sem-presenca`.
   */
  depurarChamadas(): void {
    const alvo = this.chamadasSemPresenca;
    if (!this.turmaSelecionada || !alvo.length || this.depurando) return;

    const datas = alvo
      .map((c) => this.paraData(c.data)?.toLocaleDateString('pt-BR') ?? c.data)
      .join(', ');
    const registros = alvo.reduce((acc, c) => acc + (c.totalRegistros ?? 0), 0);

    this.dialogService
      .open(DialogAlertComponent, {
        context: {
          title: 'Depurar chamadas sem presença',
          message:
            `Apagar ${alvo.length} chamada(s) de "${this.nomeTurmaSelecionada}" em que ninguém foi ` +
            `marcado presente (${registros} registro(s) no total)? Datas: ${datas}. ` +
            `Esta ação não pode ser desfeita.`,
          mostrarConfirmacao: true,
          msgConfirmacao: 'Sim, depurar estas chamadas',
        },
      })
      .onClose.subscribe((ok: boolean) => {
        if (!ok) return;
        this.depurando = true;
        this.frequenciaService
          .depurarChamadasSemPresenca(this.turmaSelecionada)
          .pipe(finalize(() => (this.depurando = false)))
          .subscribe({
            next: (res) => {
              this.carregarChamadas();
              const chamadasRemovidas = res?.totalChamadasRemovidas ?? 0;
              if (chamadasRemovidas === 0) {
                this.toastr.info('Nenhuma chamada sem presença foi encontrada.', 'Depuração');
                return;
              }
              this.toastr.success(
                `${chamadasRemovidas} chamada(s) removida(s) — ` +
                  `${res?.totalRegistrosRemovidos ?? 0} registro(s) de frequência apagados.`,
                'Depuração concluída',
              );
            },
            error: (err) =>
              this.toastr.danger(mensagemDeErro(err, 'Não foi possível depurar as chamadas.'), 'Erro'),
          });
      });
  }

  excluirChamada(c: ChamadaTurmaDto): void {
    if (!this.turmaSelecionada || !c?.data || this.excluindoData) return;

    const dataLegivel = this.paraData(c.data)?.toLocaleDateString('pt-BR') ?? c.data;
    this.dialogService
      .open(DialogAlertComponent, {
        context: {
          title: 'Excluir chamada',
          message:
            `Apagar a chamada de ${dataLegivel} em "${this.nomeTurmaSelecionada}"? ` +
            `Serão removidos ${c.totalRegistros} registro(s) de frequência — ` +
            `${c.totalPresentes} presença(s) e ${c.totalFaltas} falta(s). Esta ação não pode ser desfeita.`,
          mostrarConfirmacao: true,
          msgConfirmacao: 'Sim, excluir esta chamada',
        },
      })
      .onClose.subscribe((ok: boolean) => {
        if (!ok) return;
        this.excluindoData = c.data;
        this.frequenciaService
          .excluirChamada(this.turmaSelecionada, c.data)
          .pipe(finalize(() => (this.excluindoData = null)))
          .subscribe({
            next: (res) => {
              this.carregarChamadas();
              this.toastr.success(
                `Chamada de ${dataLegivel} excluída (${res?.registrosRemovidos ?? 0} registros).`,
                'OK',
              );
            },
            error: (err) =>
              this.toastr.danger(mensagemDeErro(err, 'Não foi possível excluir a chamada.'), 'Erro'),
          });
      });
  }

}
